<template>
  <custom-modal
    :open="open"
    header-title="设置学习计划"
    :header-subtitle="courseTitle ? `「${courseTitle}」` : ''"
    width="420px"
    :mask="true"
    :mask-closable="true"
    :show-header-close="true"
    :show-reset-button="true"
    :reset-button-text="allowSkip ? '跳过' : '取消'"
    :show-confirm-button="true"
    confirm-button-text="确认"
    :centered="true"
    body-class="px-5 py-4"
    @reset="handleReset"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  >
    <div class="flex flex-col gap-5">
      <div>
        <div class="text-sm text-gray-600 dark:text-gray-300 mb-2">{{ learnFieldLabel }}</div>
        <div
          class="flex items-center justify-between gap-2 rounded-xl bg-gray-100 dark:bg-gray-700/60 px-4 py-3 cursor-pointer hover:bg-gray-200/80 dark:hover:bg-gray-700"
          role="button"
          tabindex="0"
          @click="learnPickerOpen = true"
          @keydown.enter.prevent="learnPickerOpen = true"
        >
          <span class="text-sm text-gray-800 dark:text-gray-100">{{ learnDisplayText }}</span>
          <RightOutlined class="text-xs text-gray-400" />
        </div>
      </div>

      <div>
        <div class="text-sm text-gray-600 dark:text-gray-300 mb-2">{{ reviewFieldLabel }}</div>
        <div
          class="flex items-center justify-between gap-2 rounded-xl bg-gray-100 dark:bg-gray-700/60 px-4 py-3 cursor-pointer hover:bg-gray-200/80 dark:hover:bg-gray-700"
          role="button"
          tabindex="0"
          @click="reviewPickerOpen = true"
          @keydown.enter.prevent="reviewPickerOpen = true"
        >
          <span class="text-sm text-gray-800 dark:text-gray-100">{{ reviewDisplayText }}</span>
          <RightOutlined class="text-xs text-gray-400" />
        </div>
      </div>
    </div>

    <custom-modal
      :open="learnPickerOpen"
      :header-title="learnFieldLabel"
      width="360px"
      :mask="true"
      :mask-closable="true"
      :show-header-close="true"
      :show-reset-button="false"
      :show-confirm-button="false"
      :centered="true"
      body-class="px-3 py-2"
      @cancel="learnPickerOpen = false"
    >
      <template #customFooter>
        <div />
      </template>
      <div class="flex flex-col gap-1 max-h-[40vh] overflow-y-auto">
        <div
          v-for="n in learnOptions"
          :key="n"
          class="flex items-center justify-between rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
          :class="dailyLearn === n
            ? 'bg-[#F5F0FF] text-[#9361F4] dark:bg-purple-900/30'
            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'"
          role="button"
          tabindex="0"
          @click="selectLearn(n)"
          @keydown.enter.prevent="selectLearn(n)"
        >
          <span class="text-sm">{{ formatLearnOption(n) }}</span>
          <span v-if="dailyLearn === n" class="text-[#9361F4] text-sm">✓</span>
        </div>
      </div>
    </custom-modal>

    <custom-modal
      :open="reviewPickerOpen"
      :header-title="reviewFieldLabel"
      width="360px"
      :mask="true"
      :mask-closable="true"
      :show-header-close="true"
      :show-reset-button="false"
      :show-confirm-button="false"
      :centered="true"
      body-class="px-3 py-2"
      @cancel="reviewPickerOpen = false"
    >
      <template #customFooter>
        <div />
      </template>
      <div class="flex flex-col gap-1 max-h-[40vh] overflow-y-auto">
        <div
          v-for="n in reviewOptions"
          :key="n"
          class="flex items-center justify-between rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
          :class="dailyReview === n
            ? 'bg-[#F5F0FF] text-[#9361F4] dark:bg-purple-900/30'
            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'"
          role="button"
          tabindex="0"
          @click="selectReview(n)"
          @keydown.enter.prevent="selectReview(n)"
        >
          <span class="text-sm">{{ n }}个</span>
          <span v-if="dailyReview === n" class="text-[#9361F4] text-sm">✓</span>
        </div>
      </div>
    </custom-modal>
  </custom-modal>
</template>

<script>
import CustomModal from '@/components/Modal/index.vue'
import { RightOutlined } from '@ant-design/icons-vue'
import { normalizeTypeList } from '@/utils/courseDetailNav'

const LEARN_PRESETS = [5, 10, 15, 20, 30, 50]
const REVIEW_PRESETS = [10, 20, 30, 50]

function toPositiveInt (v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.floor(n)
}

export default {
  name: 'StudyPlanSetModal',
  components: { CustomModal, RightOutlined },
  props: {
    open: { type: Boolean, default: false },
    courseTitle: { type: String, default: '' },
    /** 课程单词总量 */
    wordCount: { type: [Number, String], default: 0 },
    /** 课程句子总量（接口字段 count） */
    sentenceCount: { type: [Number, String], default: 0 },
    /** 课程 type_list：[1]单词 [2]句子 */
    typeList: { type: Array, default: () => [] },
    /** 是否允许跳过计划（仍继续加入课程）；关闭则左侧为取消整次操作 */
    allowSkip: { type: Boolean, default: true },
    defaultDailyLearn: { type: Number, default: 20 },
    defaultDailyReview: { type: Number, default: 20 }
  },
  emits: ['confirm', 'skip', 'cancel', 'update:open'],
  data () {
    return {
      dailyLearn: 20,
      dailyReview: 20,
      learnPickerOpen: false,
      reviewPickerOpen: false,
      learnOptions: LEARN_PRESETS,
      reviewOptions: REVIEW_PRESETS
    }
  },
  computed: {
    types () {
      return normalizeTypeList(this.typeList)
    },
    wordTotal () {
      return toPositiveInt(this.wordCount)
    },
    sentenceTotal () {
      return toPositiveInt(this.sentenceCount)
    },
    hasWord () {
      if (this.types.length) return this.types.includes(1) && this.wordTotal > 0
      return this.wordTotal > 0
    },
    hasSentence () {
      if (this.types.length) return this.types.includes(2) && this.sentenceTotal > 0
      return this.sentenceTotal > 0
    },
    /** 文案单位：单词 / 句子 / 单词和句子 */
    contentUnitLabel () {
      if (this.hasWord && this.hasSentence) return '单词和句子'
      if (this.hasSentence) return '句子'
      if (this.hasWord) return '单词'
      // 列表暂无数量时，按 type_list 兜底文案
      if (this.types.length === 1 && this.types[0] === 2) return '句子'
      if (this.types.includes(1) && this.types.includes(2)) return '单词和句子'
      if (this.types.includes(2)) return '句子'
      return '单词'
    },
    learnFieldLabel () {
      return `每天学习${this.contentUnitLabel}`
    },
    reviewFieldLabel () {
      return `每天复习${this.contentUnitLabel}`
    },
    /**
     * 完成天数用的总量 = 单词数 + 句子数（按课程实际包含类型计入）
     * 天数 = ceil(总量 / 每天学习个数)
     */
    totalItems () {
      let total = 0
      if (this.types.length) {
        if (this.types.includes(1)) total += this.wordTotal
        if (this.types.includes(2)) total += this.sentenceTotal
      } else {
        total = this.wordTotal + this.sentenceTotal
      }
      // type_list 与数量字段不一致时兜底：有多少计多少
      if (!total) total = this.wordTotal + this.sentenceTotal
      return total
    },
    finishDays () {
      return this.calcFinishDays(this.dailyLearn)
    },
    learnDisplayText () {
      const days = this.finishDays
      if (days > 0) {
        return `${this.dailyLearn}个（${days}天完成学习）`
      }
      return `${this.dailyLearn}个`
    },
    reviewDisplayText () {
      return `${this.dailyReview}个`
    }
  },
  watch: {
    open (val) {
      if (val) {
        this.dailyLearn = this.defaultDailyLearn || 20
        this.dailyReview = this.defaultDailyReview || 20
        this.learnPickerOpen = false
        this.reviewPickerOpen = false
      }
    }
  },
  methods: {
    calcFinishDays (daily) {
      const n = toPositiveInt(daily)
      const total = toPositiveInt(this.totalItems)
      if (!n || !total) return 0
      return Math.max(1, Math.ceil(total / n))
    },
    formatLearnOption (n) {
      const days = this.calcFinishDays(n)
      if (days > 0) {
        return `${n}个（${days}天完成学习）`
      }
      return `${n}个`
    },
    selectLearn (n) {
      this.dailyLearn = n
      this.learnPickerOpen = false
    },
    selectReview (n) {
      this.dailyReview = n
      this.reviewPickerOpen = false
    },
    close () {
      this.$emit('update:open', false)
    },
    handleConfirm () {
      this.$emit('confirm', {
        dailyLearn: this.dailyLearn,
        dailyReview: this.dailyReview,
        finishDays: this.finishDays,
        totalItems: this.totalItems,
        contentUnit: this.contentUnitLabel
      })
      this.close()
    },
    /** 左侧按钮：可跳过时=跳过计划；否则=取消整次加入 */
    handleReset () {
      if (this.allowSkip) {
        this.$emit('skip')
        this.close()
        return
      }
      this.handleCancel()
    },
    handleCancel () {
      this.$emit('cancel')
      this.close()
    }
  }
}
</script>
